import { useState, useMemo } from 'react';
import { NavIcon } from '../../../shell/shared';
import { FilesTab } from '../consultation/FilesTab';
import { usePowerSyncQuery } from '../../../adapters/powersync';
import { qPatientFilesByEncounter, type PatientFileRow } from '@helios/data-access';
import { BeforeAfterCompare } from './BeforeAfterCompare';
import { SavedBeforeAfterPreview } from './SavedBeforeAfterPreview';
import { SavedDrawPreview } from './SavedDrawPreview';
import { DrawCharting } from './DrawCharting';

// ─── Guided workspace strip (PRD.162 Section 7.2) ───

type StripKind = 'view' | 'tool';

interface StripItem {
  key: string;
  label: string;
  icon: string;
  kind: StripKind;
  enabled: boolean;
}

const STRIP_ITEMS: StripItem[] = [
  // Major workspace views
  { key: 'all', label: 'All', icon: 'layout-grid', kind: 'view', enabled: true },
  { key: 'images', label: 'Images', icon: 'camera', kind: 'view', enabled: true },
  { key: 'before-after', label: 'Before / After', icon: 'camera', kind: 'view', enabled: true },
  // Tool / guided modes
  { key: 'draw', label: 'Draw', icon: 'edit', kind: 'tool', enabled: true },
  { key: 'compare', label: 'Compare', icon: 'list', kind: 'tool', enabled: false },
  { key: 'ai-analyze', label: 'AI Analyze', icon: 'sparkles', kind: 'tool', enabled: false },
  { key: 'upload', label: 'Upload', icon: 'folder-open', kind: 'tool', enabled: false },
];

// ─── Props ───

interface Props {
  patientId: string;
  encounterId: string;
  visitId: string;
  primary: string;
}

// ─── Component ───

export function AestheticVisualHero({ patientId, encounterId, visitId, primary }: Props) {
  const [activeKey, setActiveKey] = useState('all');
  const [activeChartId, setActiveChartId] = useState<string | null>(null);
  const [activeDrawChartId, setActiveDrawChartId] = useState<string | null>(null);

  return (
    <div>
      {/* ── Guided workspace strip ── */}
      <div
        style={{
          display: 'flex',
          gap: 4,
          padding: '10px 16px 6px',
          overflowX: 'auto',
        }}
      >
        {STRIP_ITEMS.map((item, idx) => {
          const isActive = activeKey === item.key && item.enabled;
          const isDisabled = !item.enabled;

          // Separator between views and tools
          const prevKind = idx > 0 ? STRIP_ITEMS[idx - 1].kind : item.kind;
          const showSep = item.kind !== prevKind;

          return (
            <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {showSep && (
                <div
                  style={{
                    width: 1,
                    height: 20,
                    background: 'var(--border)',
                    flexShrink: 0,
                    margin: '0 2px',
                  }}
                />
              )}
              <button
                onClick={() => {
                  if (item.enabled) setActiveKey(item.key);
                }}
                disabled={isDisabled}
                title={isDisabled ? 'Coming soon' : item.label}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '6px 12px',
                  borderRadius: 'var(--radius-full)',
                  border: isActive ? `1.5px solid ${primary}` : '1px solid var(--border)',
                  background: isActive ? `${primary}0a` : 'transparent',
                  color: isDisabled
                    ? 'var(--text-placeholder)'
                    : isActive
                      ? primary
                      : 'var(--text-muted)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: isActive ? 600 : 500,
                  cursor: isDisabled ? 'default' : 'pointer',
                  opacity: isDisabled ? 0.45 : 1,
                  transition: 'all 150ms',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                <NavIcon name={item.icon} />
                {item.label}
              </button>
            </div>
          );
        })}
      </div>

      {/* ── Active sub-view ── */}
      <div style={{ minHeight: 200 }}>
        {activeKey === 'all' && (
          <AllView
            patientId={patientId}
            encounterId={encounterId}
            onOpenBeforeAfter={(chartId) => {
              setActiveChartId(chartId);
              setActiveKey('before-after');
            }}
            onOpenDraw={(chartId) => {
              setActiveDrawChartId(chartId);
              setActiveKey('draw');
            }}
          />
        )}
        {activeKey === 'images' && (
          <div style={{ padding: 'var(--sp-5)' }}>
            <FilesTab
              patientId={patientId}
              encounterId={encounterId}
              visitId={visitId || undefined}
              hideFilters
              compact
            />
          </div>
        )}
        {activeKey === 'before-after' && (
          <BeforeAfterCompare
            patientId={patientId}
            encounterId={encounterId}
            visitId={visitId}
            initialChartId={activeChartId}
          />
        )}
        {activeKey === 'draw' && (
          <DrawCharting
            patientId={patientId}
            encounterId={encounterId}
            visitId={visitId}
            initialChartId={activeDrawChartId}
          />
        )}
      </div>
    </div>
  );
}

// ─── All: mixed visual overview (PRD.166) ───
// Sectioned layout: "Saved Chartings" (static preview cards) + "Images" (raw thumbnails).

function AllView({
  patientId,
  encounterId,
  onOpenBeforeAfter,
  onOpenDraw,
}: {
  patientId: string;
  encounterId: string;
  onOpenBeforeAfter: (chartId: string) => void;
  onOpenDraw: (chartId: string) => void;
}) {
  const { data: fileRows = [] } = usePowerSyncQuery<PatientFileRow>(
    qPatientFilesByEncounter(patientId, encounterId),
  );

  const images = useMemo(() => fileRows.filter((f) => f.kind === 'image' && f.url), [fileRows]);

  return (
    <div>
      {/* Saved Chartings section */}
      <AllSectionLabel>Saved Chartings</AllSectionLabel>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
          gap: 8,
          padding: '0 16px 8px',
        }}
      >
        <SavedBeforeAfterPreview
          patientId={patientId}
          encounterId={encounterId}
          onOpen={onOpenBeforeAfter}
        />
        <SavedDrawPreview patientId={patientId} encounterId={encounterId} onOpen={onOpenDraw} />
      </div>

      {/* Images section */}
      <AllSectionLabel>Images</AllSectionLabel>
      {images.length === 0 ? (
        <div
          style={{
            padding: 'var(--sp-4) var(--sp-5)',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          No images yet for this visit
        </div>
      ) : (
        <ImageGrid images={images} />
      )}
    </div>
  );
}

function AllSectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: '12px 16px 8px',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: 'var(--text-muted)',
      }}
    >
      {children}
    </div>
  );
}

function ImageGrid({ images }: { images: PatientFileRow[] }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
        gap: 8,
        padding: '0 16px 16px',
      }}
    >
      {images.map((file) => (
        <div
          key={file.id}
          style={{
            position: 'relative',
            aspectRatio: '1',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
            background: 'var(--bg-surface-2)',
            border: '1px solid var(--border)',
          }}
        >
          <img
            src={file.url!}
            alt={file.filename ?? ''}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
          {file.tag && file.tag !== 'other' && (
            <span
              style={{
                position: 'absolute',
                bottom: 4,
                left: 4,
                padding: '1px 6px',
                borderRadius: 'var(--radius-sm)',
                background: 'rgba(0,0,0,0.55)',
                color: '#fff',
                fontSize: 9,
                fontWeight: 500,
                textTransform: 'capitalize',
              }}
            >
              {file.tag}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
